import { useEffect, useRef, useState, useCallback } from "react";
import { Siren, MapPin, Car, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { listClaimableJobs, claimJob } from "../lib/api";
import { Button } from "./ui/button";
import { SERVICE_LABEL } from "../lib/constants";
import { cn } from "../lib/utils";

// Driver alert — fires an OS-level Notification so the phone/tablet
// plays *its own* notification sound (whatever the driver has set in
// their system settings) instead of a bundled MP3. Uses the same
// Notification permission the driver already granted for web push.
//
// Rationale: browsers deliberately don't expose the OS notification
// sound to <audio>, so playing "the phone's default sound" from an
// HTMLAudio element is impossible. Firing a Notification is the one
// path that reliably rings the system alert on Android / iOS PWA.
// Falls back to a silent no-op if the driver hasn't granted permission
// — the parallel web-push path still handles the alert in that case.
function playAlert(times = 1) {
  // Android Chrome blocks page-level `new Notification()` (it throws), which
  // made the on-call dings + ▶ Test silent on Android. Go through the
  // service worker instead — works on Android, desktop and iOS PWA, and
  // uses the phone's own notification sound.
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    if (!("serviceWorker" in navigator)) return;
    const capped = Math.max(1, Math.min(times, 3));
    navigator.serviceWorker.ready.then((reg) => {
      for (let i = 0; i < capped; i++) {
        // Stagger + unique tags so consecutive pings don't merge into one silent update.
        setTimeout(() => {
          reg.showNotification("New job available", {
            body: capped > 1 ? `${capped} jobs waiting to be claimed` : "Tap to claim",
            icon: "/maskable-192.png",
            badge: "/maskable-192.png",
            silent: false,
            vibrate: [300, 120, 300],
            tag: `driver-alert-${Date.now()}-${i}`,
            renotify: true,
            data: { url: "/driver" },
          }).catch(() => {});
        }, i * 900);
      }
    }).catch(() => {});
  } catch {
    /* permission or notification support blocked — silent fallback */
  }
}

// Title-flash so the driver notices the tab even if it's backgrounded.
// Reads document.title once at flash time so it inherits whatever the
// tenant's branding.app_title resolved to on page load — no hardcoded
// company name.
function flashTitle(claimableCount) {
  if (!document) return;
  const original = document.title || "Driver";
  if (claimableCount === 0) {
    document.title = original;
    return;
  }
  let on = true;
  const tag = `🚨 ${claimableCount} job${claimableCount > 1 ? "s" : ""} waiting`;
  const flash = () => {
    document.title = on ? tag : original;
    on = !on;
  };
  flash();
  const intv = setInterval(flash, 1000);
  // Stop flashing once the driver focuses the tab — they've seen it.
  const stop = () => {
    if (!document.hidden) {
      clearInterval(intv);
      document.title = original;
      document.removeEventListener("visibilitychange", stop);
    }
  };
  document.addEventListener("visibilitychange", stop);
  return () => {
    clearInterval(intv);
    document.title = original;
    document.removeEventListener("visibilitychange", stop);
  };
}

export default function AvailableJobsPanel({ truckId, truckName, onClaimed, isOnCall }) {
  const [claimable, setClaimable] = useState([]);
  const [claiming, setClaiming] = useState(null);
  // Sound mute is now controlled from the top-row notification button; this
  // panel just honours the stored preference when deciding whether to ding.
  const muted = localStorage.getItem("driver_oncall_mute") === "1";
  const prevIdsRef = useRef(new Set());
  // First successful fetch only SEEDS the seen-set (no ding for jobs that
  // were already sitting there when the panel mounted). Every fetch after
  // that alerts on genuinely new ids — including the first job into an
  // empty overnight list, which the old `prev.size > 0` guard wrongly muted.
  const seededRef = useRef(false);
  const titleCleanupRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const data = await listClaimableJobs();
      const list = Array.isArray(data) ? data : [];
      const prev = prevIdsRef.current;
      const newOnes = list.filter((j) => !prev.has(j.id));
      // Commit the seen-set BEFORE any alerting. A failure inside the alert
      // path must never leave these ids "unseen" — that's what caused the
      // endless ding loop (Android's page-context `new Notification()`
      // throws, which skipped this line and re-alerted every poll).
      prevIdsRef.current = new Set(list.map((j) => j.id));
      const seeded = seededRef.current;
      seededRef.current = true;
      if (seeded && newOnes.length > 0) {
        // Ding once per job WAITING (1st job = 1 ding, 2 waiting = 2 dings,
        // 3rd = 3…), capped at 5 so the cab isn't overwhelmed. One burst per
        // arrival — never repeats for jobs already announced.
        if (!muted) playAlert(Math.min(5, list.length));
        toast.success(
          `🚨 ${newOnes.length} new job${newOnes.length > 1 ? "s" : ""} available`,
          { duration: 8000 },
        );
      }
      // Title-flash whenever we have claimable jobs.
      if (titleCleanupRef.current) titleCleanupRef.current();
      titleCleanupRef.current = flashTitle(list.length);
      setClaimable(list);
    } catch {
      /* swallow — non-fatal, will retry next tick */
    }
  }, [muted]);

  // Ask for notification permission once when on-call mode kicks in.
  useEffect(() => {
    if (!isOnCall) return;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, [isOnCall]);

  // Poll every 15 s (faster than the main jobs list — drivers want to grab work quickly).
  useEffect(() => {
    if (!isOnCall) {
      setClaimable([]);
      prevIdsRef.current = new Set();
      seededRef.current = false;
      return;
    }
    refresh();
    const t = setInterval(refresh, 15000);
    return () => {
      clearInterval(t);
      if (titleCleanupRef.current) titleCleanupRef.current();
    };
  }, [isOnCall, refresh]);

  const handleClaim = async (job) => {
    if (!truckId) {
      toast.error("Pick a truck first.");
      return;
    }
    setClaiming(job.id);
    try {
      await claimJob(job.id, truckId);
      toast.success(`Claimed ${job.customer_name || job.id.slice(0, 6)} for ${truckName}`);
      setClaimable((cs) => cs.filter((j) => j.id !== job.id));
      onClaimed?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error("Another driver just claimed it.");
      } else {
        toast.error(detail || "Couldn't claim — try refreshing.");
      }
      refresh();
    } finally {
      setClaiming(null);
    }
  };

  if (!isOnCall) return null;

  return (
    <section
      className="mb-6 bg-emerald-50 border-2 border-emerald-400 dark:bg-emerald-950/40 dark:border-emerald-600 rounded-lg p-4"
      data-testid="available-jobs-panel"
    >
      <header className="flex items-center gap-2 mb-2">
        <Siren className={cn(
          "h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-300",
          claimable.length > 0 && "animate-pulse",
        )} />
        <h3 className="font-display font-black text-lg text-emerald-900 dark:text-emerald-100 tracking-tight whitespace-nowrap">
          On-Call · Available Jobs
          <span className="ml-2 text-sm font-bold text-emerald-700 dark:text-emerald-300">
            ({claimable.length})
          </span>
        </h3>
      </header>

      {claimable.length === 0 ? (
        <div className="text-center text-emerald-800 dark:text-emerald-200 py-3 text-sm">
          No unclaimed jobs right now — you&apos;ll hear a beep when one comes in.
        </div>
      ) : (
        <div className="space-y-3">
          {claimable.map((j) => (
            <div
              key={j.id}
              className="bg-white dark:bg-slate-900 border-2 border-emerald-400 dark:border-emerald-600 rounded-md p-4 shadow-md animate-[pulse_3s_ease-in-out_infinite]"
              style={{ animationDuration: "3s" }}
              data-testid={`claim-row-${j.id}`}
            >
              <div className="mb-3">
                <div className="font-display font-black text-xl text-slate-900 dark:text-slate-100 leading-tight">
                  {j.customer_name || "Unknown"}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {j.aa_job_id && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-100 dark:bg-blue-900 dark:text-blue-200 px-1.5 py-0.5 rounded">
                      AA {j.aa_job_id}
                    </span>
                  )}
                  {j.work_order_number && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-100 dark:bg-indigo-900 dark:text-indigo-200 px-1.5 py-0.5 rounded">
                      WO {j.work_order_number}
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-200 mt-2 flex items-center gap-1.5">
                  <Car className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="font-semibold">{j.vehicle || "Vehicle TBC"}</span>
                  <span className="mx-1 text-slate-300 dark:text-slate-600">·</span>
                  <span>{SERVICE_LABEL[j.service_type] || j.service_type}</span>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-300 mt-1 flex items-start gap-1.5">
                  <MapPin className="h-4 w-4 shrink-0 text-slate-400 mt-0.5" />
                  <span>{j.address || "Address TBC"}</span>
                </div>
              </div>
              <Button
                onClick={() => handleClaim(j)}
                disabled={claiming === j.id}
                className="w-full h-14 text-lg font-black uppercase tracking-wide bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white shadow-lg shadow-emerald-600/30"
                data-testid={`claim-btn-${j.id}`}
              >
                <CheckCircle2 className="h-6 w-6" />
                {claiming === j.id ? "Claiming…" : "Claim Job"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
